import React, { memo, useEffect, useEffectEvent, useLayoutEffect, useRef } from "react";
import { subscribe } from "valtio";
import "./zone-menu.css";

import { usePreviewContext } from "../../contexts/preview-context";
import { useAppContext } from "../../hooks/useAppContext";
import { useMessage } from "../../hooks/useMessage";
import { useGetPreviewBoxStore } from "../../hooks/usePreviewBox";
import { useResizeObserver } from "../../hooks/useResizeObserver";

const margin = 10;

export const ZoneMenu = memo(({ children }: { children?: React.ReactNode }) => {
	const { editor } = useAppContext();
	const previewContext = usePreviewContext();
	const previewBoxStore = useGetPreviewBoxStore();

	/** To position the zone menu */
	const zoneMenuRef = useRef<HTMLDivElement>(null);

	/** To measure content size */
	const zoneMenuContentRef = useRef<HTMLDivElement>(null);

	/**
	 * Position the zone menu against the selected zone.
	 *
	 * Reads live state (selected zone, preview box and slideshow transform) instead of
	 * receiving them as arguments. The preview box (bottom-menu growth) and the slideshow
	 * transform (preview re-fit) settle through different channels when entering transform
	 * mode, so a snapshot captured in a render closure can be stale relative to the
	 * transform. Reading both live at call time keeps them consistent for every trigger.
	 */
	const updateZoneMenu = useEffectEvent(() => {
		if (!zoneMenuRef.current) return;

		const zone = editor.selection.getCurrentZone(); // Currently use selected zone from core
		if (!zone) {
			zoneMenuRef.current.style.display = "none";
			return;
		}

		const previewRect = previewBoxStore.previewBox;
		if (!zoneMenuContentRef.current || !previewRect) return;
		zoneMenuRef.current.style.display = "block";

		const dipToScreenTransform = previewContext.getSpreadToScreenTransform(
			zone.spread,
			undefined,
		);
		if (!dipToScreenTransform) return;

		const outerBoundsRect = dipToScreenTransform.transformRect(zone.spreadOuterBounds);
		const zoneMenuContentRect = zoneMenuContentRef.current.getBoundingClientRect();

		const zoneBottomCenter = eXpress.math.point(
			outerBoundsRect.left + outerBoundsRect.width / 2,
			outerBoundsRect.top + outerBoundsRect.height + margin,
		);
		const bottomZoneMenuRect = eXpress.math.rect(
			zoneBottomCenter.left - zoneMenuContentRect.width / 2,
			zoneBottomCenter.top,
			zoneMenuContentRect.width,
			zoneMenuContentRect.height,
		);

		const zoneTopCenter = eXpress.math.point(
			outerBoundsRect.left + outerBoundsRect.width / 2,
			outerBoundsRect.top - margin,
		);
		const topZoneMenuRect = eXpress.math.rect(
			zoneTopCenter.left - zoneMenuContentRect.width / 2,
			zoneTopCenter.top - zoneMenuContentRect.height,
			zoneMenuContentRect.width,
			zoneMenuContentRect.height,
		);

		// Check if top or bottom should be used
		const previewRect2 = eXpress.math.rect(previewRect);
		const areaInViewOnTop = previewRect2.intersect(topZoneMenuRect).area();
		const areaInViewOnBottom = previewRect2.intersect(bottomZoneMenuRect).area();

		// Move inside the previewRect with a margin
		let zoneMenuRect = bottomZoneMenuRect;
		if (areaInViewOnTop > areaInViewOnBottom) {
			zoneMenuRect = topZoneMenuRect.movedInRange(previewRect2.shrink(margin));
		} else {
			zoneMenuRect = bottomZoneMenuRect.movedInRange(previewRect2.shrink(margin));
		}

		// Position at bottom center of bounding box
		zoneMenuRef.current.style.transform = `translate3d(${zoneMenuRect.left}px, ${zoneMenuRect.top}px, 0)`;
		zoneMenuRef.current.style.width = `${zoneMenuRect.width}px`;
		zoneMenuRef.current.style.height = `${zoneMenuRect.height}px`;
	});

	useResizeObserver(zoneMenuContentRef, () => updateZoneMenu());

	/** Position on mount; all later changes are handled by the subscriptions below. */
	useLayoutEffect(() => {
		updateZoneMenu();
	}, []);

	/**
	 * Re-position when the available preview area or the slideshow transform changes
	 * (preview zoom/pan, or the preview re-fit after the bottom menu grows when entering
	 * transform mode).
	 *
	 * The subscriptions are stable (keyed only on stable refs) so they are never torn
	 * down and re-created on a preview-box change. That avoids missing the (synchronous)
	 * onTransformChange that the slideshow emits while re-fitting, which previously left
	 * the menu positioned behind the bottom menu until the next interactive update. CRE-6338
	 */
	const { slideShowPreviewRef } = previewContext;
	useEffect(
		function updateZoneMenuOnTransformChange() {
			const slideShow = slideShowPreviewRef.current?.slideShow;
			const unsubscribeTransform = slideShow?.onTransformChange.subscribe(() =>
				updateZoneMenu(),
			);
			const unsubscribePreviewBox = subscribe(previewBoxStore, () => updateZoneMenu());

			return () => {
				unsubscribeTransform?.();
				unsubscribePreviewBox();
			};
		},
		[slideShowPreviewRef, previewBoxStore],
	);

	/** Update on message that can change the zone (zone moved, resized, rotated, other selection) */
	useMessage(
		(message) => {
			// Because message is synchronous, we read the selected zone for editor
			const zone = editor.selection.getCurrentZone();

			if (
				eXpress.core.isSelectedZoneChanged(message) ||
				eXpress.core.isZoneAffected(message, zone)
			)
				updateZoneMenu();
		},
		{
			captureInteractiveMessages: true,
		},
	);

	return (
		<div ref={zoneMenuRef} className="zone-menu">
			<div ref={zoneMenuContentRef} className="zone-menu__content">
				{children}
			</div>
		</div>
	);
});
ZoneMenu.displayName = "ZoneMenu";
